import { z } from 'zod';

/**
 * Schemes safe to render into an href. http(s) for web links, mailto/tel for
 * contact links; `javascript:` and `data:` are excluded as XSS vectors.
 */
export const SAFE_URL_PROTOCOL = /^(https?|mailto|tel)$/;

const safeUrlSchema = z.url({ protocol: SAFE_URL_PROTOCOL });

/**
 * Whether `href` is a valid URL with a safe scheme. Use before turning a
 * stored value into an anchor so an unsafe scheme can't become clickable.
 */
export const isSafeHref = (href: string): boolean =>
  safeUrlSchema.safeParse(href).success;

// No `.` in the scheme body — real schemes (http, mailto, tel...) never
// have one, but a bare domain routinely does, so `nike.com:8080` (a host
// with a port, no scheme at all) would otherwise be misread as pre-schemed.
const HAS_SCHEME = /^[a-z][a-z0-9+-]*:/i;

/**
 * Prefixes a bare domain with `https://` so e.g. `nike.com` becomes a
 * fully-qualified href instead of resolving as a broken relative link.
 * Leaves already-schemed values (any `scheme:`, safe or not — `isSafeHref`
 * is still the gate for safety) and blank input untouched.
 */
export const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || HAS_SCHEME.test(trimmed)) {
    return trimmed;
  }
  // A protocol-relative URL ("//example.com") already implies https(s) —
  // treating it as schemeless would double up the slashes into `https:////`.
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }
  // `#`, a site-relative path ("/returns"), or a query-only value aren't
  // domains to prefix — "#" in particular is the standard no-op placeholder
  // href, and multiple links commonly share it. Prepending https:// would
  // collapse every one of them to the same nonsense "https://#" string.
  if (/^[#/?]/.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};
